// dashboardEvents — single WS client for all dashboard push channels.
//
// One tab → one WebSocket at /api/dashboard/ws?token=<jwt>. Features
// subscribe to topics (e.g. "webrtc:<deviceId>", "coverage:<projectId>:<bv>",
// "chat"). Each topic can have multiple listeners — bus fans incoming frames
// to every listener for the frame's topic.
//
// Connection is lazily opened on the first subscribe and stays open as long
// as at least one listener exists. Heartbeat is server-driven (pings); we
// reconnect on close with exponential backoff up to 30s.

type Listener = (frame: Record<string, unknown>) => void;

const WS_PATH = '/api/dashboard/ws';

let ws: WebSocket | null = null;
let openPromise: Promise<void> | null = null;
let backoff = 1000;
const listeners = new Map<string, Set<Listener>>();
// Topics we've told the server about; resent on reconnect.
const subscribedTopics = new Set<string>();

function send(msg: object): void {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  try {
    ws.send(JSON.stringify(msg));
  } catch {
    /* closed */
  }
}

function ensureSocket(): Promise<void> {
  if (ws && ws.readyState === WebSocket.OPEN) return Promise.resolve();
  if (openPromise) return openPromise;

  const token = localStorage.getItem('auth_token') ?? '';
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const url = `${proto}://${location.host}${WS_PATH}?token=${encodeURIComponent(token)}`;

  openPromise = new Promise<void>((resolve, reject) => {
    const sock = new WebSocket(url);
    ws = sock;

    sock.addEventListener('open', () => {
      backoff = 1000;
      if (subscribedTopics.size > 0) {
        send({ type: 'subscribe', topics: Array.from(subscribedTopics) });
      }
      resolve();
    });

    sock.addEventListener('message', (e) => {
      let frame: any;
      try {
        frame = JSON.parse(e.data as string);
      } catch {
        return;
      }
      if (!frame || typeof frame !== 'object') return;
      const topic = frame.topic;
      if (typeof topic !== 'string') return;
      const set = listeners.get(topic);
      if (!set) return;
      for (const fn of set) {
        try {
          fn(frame);
        } catch (err) {
          console.warn('[dashboardEvents] listener error', err);
        }
      }
    });

    sock.addEventListener('close', () => {
      ws = null;
      openPromise = null;
      // Only reconnect if there are active listeners. Otherwise stay closed.
      if (listeners.size === 0) return;
      const delay = Math.min(backoff, 30_000);
      backoff = Math.min(backoff * 2, 30_000);
      setTimeout(() => {
        ensureSocket().catch(() => {
          /* retry on next event */
        });
      }, delay);
    });

    sock.addEventListener('error', () => {
      // close handler will fire; reject the pending open promise so callers
      // see the failure without hanging.
      reject(new Error('dashboard WS error'));
    });
  });

  return openPromise;
}

/** Send an inbound message to the server. Used by feature modules that
 *  push outbound events (input forwarding, ICE candidates). Opens the
 *  socket lazily if not yet connected. Fire-and-forget — caller doesn't
 *  await a response. */
export function sendMessage(msg: object): void {
  ensureSocket()
    .then(() => send(msg))
    .catch(() => {
      /* reconnect handles it */
    });
}

/** Wait until the dashboard WS is open. After `subscribe(topic, fn)` is
 *  called, awaiting this guarantees the subscribe frame has been flushed
 *  to the server (microtask order on the shared ensureSocket promise).
 *  Use before performing an HTTP action whose async reply is published on
 *  the just-subscribed topic — closes the subscribe-vs-publish race. */
export function awaitConnection(): Promise<void> {
  return ensureSocket();
}

/** Forward a tap/swipe/pointer/pointerMove input op to the device via the
 *  dashboard bus. Hot path — fires at up to 60 Hz under live touch
 *  control. Server-side handler authorizes + forwards via the device's
 *  active tunnel. */
export function sendInput(
  deviceId: string,
  op: 'tap' | 'swipe' | 'pointer' | 'pointerMove' | 'key',
  body: Record<string, unknown>,
): void {
  sendMessage({ type: 'input', deviceId, op, body });
}

/** Broadcast a live-annotation stroke segment to every other dashboard
 *  viewer of the same device. Pure dashboard ↔ dashboard — device is not
 *  involved. Server re-broadcasts to topic `annotation:<deviceId>` and
 *  skips the sender. */
export function sendAnnotation(
  deviceId: string,
  strokeId: string,
  seq: number,
  points: Array<{ x: number; y: number }>,
  color: string,
  done: boolean,
): void {
  sendMessage({ type: 'annotate', deviceId, strokeId, seq, points, color, done });
}

/** Forward a dashboard-side ICE candidate to the device via the bus.
 *  Replaces the POST /webrtc-ice round-trip. {@code sessionId} scopes the
 *  candidate to a specific peer on the device — required for multi-viewer. */
export function sendIceCandidate(
  deviceId: string,
  sessionId: string,
  candidate: string,
  sdpMid: string | null,
  sdpMLineIndex: number,
): void {
  sendMessage({ type: 'webrtcIceOut', deviceId, sessionId, candidate, sdpMid, sdpMLineIndex });
}

/** Keystrokes / paste for the agent CLI a runner hosts for a controlled
 *  session. The server checks the sender may control the session and
 *  forwards to the runner's PTY. */
export function sendAgentInput(sessionId: string, data: string): void {
  sendMessage({ type: 'agentInput', sessionId, data });
}

export function sendAgentResize(sessionId: string, cols: number, rows: number): void {
  sendMessage({ type: 'agentResize', sessionId, cols, rows });
}

/** Subscribe a listener to a topic. Returns an unsubscribe function. The
 *  socket is opened lazily on the first call. */
export function subscribe(topic: string, fn: Listener): () => void {
  let set = listeners.get(topic);
  if (!set) {
    set = new Set();
    listeners.set(topic, set);
  }
  set.add(fn);

  if (!subscribedTopics.has(topic)) {
    subscribedTopics.add(topic);
    ensureSocket()
      .then(() => send({ type: 'subscribe', topics: [topic] }))
      .catch(() => {
        /* reconnect path will replay */
      });
  }

  return () => {
    const s = listeners.get(topic);
    if (!s) return;
    s.delete(fn);
    if (s.size === 0) {
      listeners.delete(topic);
      subscribedTopics.delete(topic);
      send({ type: 'unsubscribe', topics: [topic] });
    }
  };
}
